import { useState } from 'react'
import { Link } from 'react-router-dom'
import Icon, { type IconName } from './Icon'

interface QuickStartBannerProps {
  storageKey?: string   // localStorage key, 默认 'hasSeenQuickStart'
  onDismiss?: () => void
}

/**
 * 首页引导面板 - 首次访问显示三步指南
 * 暗色/明亮模式都适配 (颜色靠 CSS 变量)
 */
function QuickStartBanner({
  storageKey = 'hasSeenQuickStart',
  onDismiss,
}: QuickStartBannerProps) {
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem(storageKey) === 'true'
  })
  const [step, setStep] = useState(0)

  if (dismissed) return null

  const handleDismiss = () => {
    localStorage.setItem(storageKey, 'true')
    setDismissed(true)
    onDismiss?.()
  }

  const steps: { icon: IconName; title: string; description: string; cta: { text: string; to: string } | null }[] = [
    {
      icon: 'book',
      title: '选章节',
      description: '从 Ch1「先活下来」开始，逐章解锁。每一章都是 Z 哥心法的核心模块。',
      cta: { text: '去章节地图', to: '/#chapters' },
    },
    {
      icon: 'card',
      title: '玩 5 张卡 = 1 小节',
      description: '每小节 5 张卡片。读完场景、选答案、点"翻开看答案"看 Z 哥详解。',
      cta: null,
    },
    {
      icon: 'trophy',
      title: '解锁勋章',
      description: '完成章节、保持正确率、坚持训练 —— 解锁 10 枚专属勋章。',
      cta: null,
    },
  ]

  const current = steps[step] ?? steps[0]!
  const isLast = step === steps.length - 1

  return (
    <div className="pixel-box pixel-box-bright p-4 mb-4 relative bg-[color:var(--color-bg-elevated)]">
      {/* 右上角关闭按钮 */}
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-[color:var(--color-text-dim)] hover:text-[color:var(--color-bear)] transition-colors p-1"
        title="跳过引导"
      >
        <Icon name="cross" size={11} />
      </button>

      <div className="font-pixel text-[10px] text-[color:var(--color-gold)] mb-2 tracking-wider">
        ► 新人引导 · {step + 1} / {steps.length}
      </div>

      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0 text-[color:var(--color-gold)] mt-1">
          <Icon name={current.icon} size={30} />
        </div>
        <div className="flex-1">
          <h3 className="font-pixel text-base text-[color:var(--color-text-bright)] mb-1">
            {current.title}
          </h3>
          <p className="font-pixel-body text-sm text-[color:var(--color-text)] leading-snug">
            {current.description}
          </p>
        </div>
      </div>

      {/* 进度指示点 */}
      <div className="flex items-center justify-center gap-1 mb-3">
        {steps.map((_, i) => (
          <span
            key={i}
            className={
              'w-2 h-2 pixel-box ' +
              (i === step
                ? 'bg-[color:var(--color-bull)] border-[color:var(--color-bull)]'
                : 'bg-[color:var(--color-bg-base)] border-[color:var(--color-border)]')
            }
          />
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {!isLast ? (
          <>
            <button
              onClick={() => setStep(step + 1)}
              className="pixel-button pixel-button-primary flex-1"
            >
              下一步 →
            </button>
            <button onClick={handleDismiss} className="pixel-button">
              我知道了
            </button>
          </>
        ) : (
          <>
            {current.cta && (
              <Link
                to={current.cta.to}
                onClick={handleDismiss}
                className="pixel-button pixel-button-primary flex-1 text-center"
              >
                ► {current.cta.text}
              </Link>
            )}
            <button onClick={handleDismiss} className="pixel-button">
              开始训练
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default QuickStartBanner